import { useGetInsightsQuery } from "@/redux/feature/product/productApi";
import { useParams } from "react-router-dom";
import CompetitorAnalysis from "./CompetitorAnalysis";

type InsightsModalProps = {
  onSelectPersona: (persona: string) => void;
  setIsInsightsModalOpen: (isOpen: boolean) => void;
};

const InsightsModal = ({
  onSelectPersona,
  setIsInsightsModalOpen,
}: InsightsModalProps) => {
  const { productId } = useParams();
  const { data, isLoading, isError } = useGetInsightsQuery({
    productId: productId as string,
  });

  if (isLoading) {
    return (
      <div className="bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading insights...</p>
        </div>
      </div>
    );
  }

  if (isError || !data.data) {
    return (
      <div className="bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-4 h-4 text-red-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-red-600 font-medium">Failed to load insights</p>
        </div>
      </div>
    );
  }

  const {
    brand_videos,
    industry_average,
    competitor_videos,
    difference_with_industry_average,
    missing_personas,
    lagging_videos,
  } = data.data;

  return (
    <div>
      <div>
        {/* Header Card */}
        <div className="pb-2 border-b mb-8">
          <h1 className="text-xl font-semibold text-gray-900">Insights</h1>
        </div>

        <div className="space-y-8">
          {/* Brand Videos Section */}
          <div className="px-6 py-5 bg-white rounded-2xl shadow-sm border overflow-hidden">
            <h6 className="text-gray-900 text-lg mb-3.5 font-semibold">
              Opportunity to scale : Your video volume
            </h6>
            <div className="flex md:flex-row flex-col gap-y-4 justify-between">
              <div>
                <h3 className="text-base font-medium text-gray-900">
                  Brand Videos
                </h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                    {brand_videos?.total_running} Total Running
                  </span>
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium text-gray-900">
                  Industry average Videos
                </h3>
                <div className="flex items-center md:justify-end gap-2 mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                    {Math.floor(industry_average?.total_running)} Total Running
                  </span>
                </div>
              </div>
            </div>

            {difference_with_industry_average.total_running !== 0 && (
              <div className="py-2 rounded-full border border-gray-400 mt-4">
                <p className="text-center text-gray-400 text-sm">
                  You are creating{" "}
                  <span className="text-gray-950 font-semibold">
                    {" "}
                    {difference_with_industry_average?.total_running} fewer
                    videos
                  </span>{" "}
                  per product than the industry average.
                </p>
              </div>
            )}
          </div>

          {/* Missing Personas Section */}
          {missing_personas.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border">
              <div className="px-6 py-5 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">
                  Missing Personas
                </h3>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-3">
                  {missing_personas.map((item: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => {
                        setIsInsightsModalOpen(false);
                        onSelectPersona(item);
                      }}
                      className="border rounded-lg px-2 py-1 text-sm leading-5 tracking-normal font-medium hover:bg-gray-100 transition"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Competitor Videos Section */}
          <CompetitorAnalysis competitor_videos={competitor_videos} />

          {/* Lagging Videos Section */}
          {/* <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                Lagging Videos
              </h3>
            </div>

            <div className="p-6">
              {lagging_videos && lagging_videos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {lagging_videos.map((video: VideoData, index: number) => (
                    <ProductCard
                      key={`lagging-${index}`}
                      currentlyPlaying={currentlyPlaying}
                      setCurrentlyPlaying={setCurrentlyPlaying}
                      video={video}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-green-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="text-green-700 font-semibold">
                    All videos performing well!
                  </p>
                  <p className="text-green-600 text-sm mt-1">
                    No lagging videos found
                  </p>
                </div>
              )}
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default InsightsModal;
